import React, { useMemo, useState } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { ListSortMenu, type ListSortOption } from '@/shared/components/ListSortMenu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { useProjectUsers, useSubmittalPackages } from '../hooks';
import {
  SUBMITTAL_STATUS_LABELS,
  type SubmittalFilters,
  type SubmittalSortKey,
  type SubmittalSortState,
  type SubmittalStatus,
} from '../types';

interface Props {
  projectId: number | undefined;
  filters: SubmittalFilters;
  onFiltersChange: (filters: SubmittalFilters) => void;
  totalCount: number;
  filteredCount: number;
  sort: SubmittalSortState[];
  onSortChange: (sort: SubmittalSortState[]) => void;
}

const SORT_OPTIONS: readonly ListSortOption<SubmittalSortKey>[] = [
  { key: 'submittal_number', label: 'Submittal number' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'reviewer', label: 'Reviewer' },
  { key: 'required_response_date', label: 'Due date' },
  { key: 'days_open', label: 'Days open' },
];

export const SubmittalsFilters: React.FC<Props> = ({
  projectId,
  filters,
  onFiltersChange,
  totalCount,
  filteredCount,
  sort,
  onSortChange,
}) => {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const { data: packages = [] } = useSubmittalPackages(projectId);
  const { data: users = [] } = useProjectUsers(projectId);

  const activeFilterCount = useMemo(
    () =>
      [
        filters.status,
        filters.package_id,
        filters.reviewer_id,
        filters.overdue,
        filters.current_revision_only === false,
      ].filter(Boolean).length,
    [
      filters.current_revision_only,
      filters.overdue,
      filters.package_id,
      filters.reviewer_id,
      filters.status,
    ]
  );
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.search ||
          filters.status ||
          filters.package_id ||
          filters.reviewer_id ||
          filters.overdue ||
          filters.current_revision_only === false
      ),
    [filters]
  );

  const update = (patch: Partial<SubmittalFilters>) => onFiltersChange({ ...filters, ...patch });
  const clearDialogFilters = () =>
    onFiltersChange({
      ...(filters.search ? { search: filters.search } : {}),
      current_revision_only: true,
    });
  const clearAllFilters = () => onFiltersChange({ current_revision_only: true });

  return (
    <div className="space-y-3" data-tour-id="submittals-filters">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 md:max-w-[380px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by number, title, spec section…"
            value={filters.search ?? ''}
            onChange={(e) => update({ search: e.target.value || undefined })}
            className="w-full pl-8"
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="hidden gap-2 md:inline-flex"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>

        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="hidden md:inline-flex"
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="hidden gap-1 md:inline-flex"
          >
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        )}

        <span className="ml-auto hidden text-sm text-muted-foreground md:inline">
          Showing {filteredCount} of {totalCount}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 px-3 py-2 md:hidden">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-2"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>
        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="w-full"
        />
      </div>

      <div className="flex items-center justify-between gap-3 md:hidden">
        {hasActiveFilters ? (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="gap-1">
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        ) : (
          <span />
        )}
        <span className="text-sm text-muted-foreground">
          Showing {filteredCount} of {totalCount}
        </span>
      </div>

      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Submittal Filters</DialogTitle>
            <DialogDescription>Refine the submittals shown on this page.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <FilterField label="Status">
              <Select
                value={filters.status ?? 'all'}
                onValueChange={(value) =>
                  update({ status: value === 'all' ? undefined : (value as SubmittalStatus) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {(Object.keys(SUBMITTAL_STATUS_LABELS) as SubmittalStatus[]).map((status) => (
                    <SelectItem key={status} value={status}>
                      {SUBMITTAL_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Package">
              <Select
                value={filters.package_id ?? 'all'}
                onValueChange={(value) =>
                  update({ package_id: value === 'all' ? undefined : value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All packages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All packages</SelectItem>
                  <SelectItem value="unpackaged">Unpackaged</SelectItem>
                  {packages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id}>
                      {pkg.package_id} - {pkg.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Reviewer">
              <Select
                value={filters.reviewer_id ? String(filters.reviewer_id) : 'all'}
                onValueChange={(value) =>
                  update({ reviewer_id: value === 'all' ? undefined : Number(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All reviewers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reviewers</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Due Status">
              <Select
                value={filters.overdue ? 'yes' : 'all'}
                onValueChange={(value) => update({ overdue: value === 'yes' ? true : undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All due dates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All due dates</SelectItem>
                  <SelectItem value="yes">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Revisions">
              <Select
                value={filters.current_revision_only === false ? 'all' : 'current'}
                onValueChange={(value) => update({ current_revision_only: value === 'current' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Current revision" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current revision</SelectItem>
                  <SelectItem value="all">All revisions</SelectItem>
                </SelectContent>
              </Select>
            </FilterField>
          </div>

          <DialogFooter className="gap-2 sm:space-x-0">
            <Button
              variant="outline"
              onClick={clearDialogFilters}
              disabled={activeFilterCount === 0}
            >
              Clear All
            </Button>
            <DialogClose asChild>
              <Button>Done</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export default SubmittalsFilters;
